import { Location } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { MatDialogRef } from '@angular/material';
import { Router, RouterOutlet } from '@angular/router';
import { IImportSettings } from '@models/import/import-settings';
import { ImporterState } from '@models/import/importer-state';
import { IPollFileImport } from '@models/import/poll-file-import';
import { IMenuItem } from '@models/menu/menu-item';
import { WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { ImportConflictsService } from '@services/import/import-conflicts.service';
import { ImportService } from '@services/import/import.service';
import { MenuService } from '@services/menu.service';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { ImportDialogTabs } from '../dialog-tabs';
import { ImportConflictsComponent } from '../import-conflicts/import-conflicts.component';

@Component({
  selector: 'app-import-dialog',
  templateUrl: './import-dialog.component.html',
  styleUrls: ['./import-dialog.component.scss']
})
export class ImportDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() updatedAccounts: EventEmitter<any> = new EventEmitter<any>();
  public isMarketRate = false;
  public cancelButtonDisabled = true;
  public continueButtonDisabled = true;
  public halfSized = false;
  public importSettingsPromise: Promise<IImportSettings>;
  public isConflictsDialogOpened = false;
  public maximized = false;
  public tabs: IMenuItem[] = [];
  public numberOfActiveConflicts = 0;
  private readonly subscriptions: Subscription[] = [];

  @ViewChild(RouterOutlet, { static: false }) outlet: RouterOutlet;

  constructor(
    public dialogRef: MatDialogRef<ImportDialogComponent>,
    private readonly router: Router,
    private readonly location: Location,
    private readonly menu: MenuService,
    private readonly importService: ImportService,
    private readonly conflictService: ImportConflictsService,
    private readonly windowService: WindowService
  ) {}

  @ViewChild('conflictsContainer', { read: ViewContainerRef, static: false })
  public containerRef: ViewContainerRef;
  public windowRef: WindowRef = null;

  async ngOnInit() {
    this.importSettingsPromise = this.importService.getImportSettings();
    this.importSettingsPromise.then(res => {
      if (res && res.ActiveImport && res.ActiveImport.import_id > 0) {
        if (res.ActiveImport.is_market_rate) {
          this.onTabClicked(this.tabs[1]);
        } else {
          this.onTabClicked(this.tabs[0]);
        }
      } else {
        this.onTabClicked(this.tabs[0]);
      }
    });
    this.setDialogListeners();
    this.tabs = ImportDialogTabs.getDialogTabs();

    const conflicts = await this.conflictService.GetNumberOfConflicts().toPromise();
    this.numberOfActiveConflicts = conflicts.NumberActiveConflicts;
  }

  ngAfterViewInit() {
    this.outlet.deactivate();
  }

  ngOnDestroy() {
    this.outlet.deactivate();
    this.subscriptions.forEach(subs => subs.unsubscribe());
  }
  /*******************
   * Dialog Controls *
   *******************/

  public close(event) {
    if (this.importService.importStatus !== ImporterState.fileUploading) {
      this.dialogRef.close();
    } else {
      Swal.fire('Unable To Close', 'File upload in progress', 'warning');
    }
  }

  public toggleHalfSized() {
    this.importService.isHalfSized = !this.importService.isHalfSized;
  }
  public toggleMaximized() {
    this.importService.isMaximized = !this.importService.isMaximized;
  }

  public onTitlebarDoubleClick() {
    this.toggleMaximized();
  }

  private setDialogListeners() {
    this.dialogRef.afterClosed().subscribe(() => {
      //   this.router.navigate(['../'], { relativeTo: this.route });
      // });

      const selectedProduct = this.menu.selectedProduct;
      const selectedOption = this.menu.selectedOption;

      if (selectedOption.group === 'all') {
        this.router.navigateByUrl(selectedOption.path).catch(err => {
          console.error(err);
        });
      } else if (selectedOption.group === 'product') {
        this.router.navigateByUrl(`${selectedOption.path}/${selectedProduct.ProductId}`).catch(err => {
          console.error(err);
        });
      }
    });

    this.importService.importStatusListener$.subscribe(importStatus => {
      switch (importStatus) {
        case ImporterState.idle:
          this.tabs.forEach(tab => (tab.disabled = false));
          this.dialogRef.disableClose = false;
          this.cancelButtonDisabled = false;
          break;
        case ImporterState.fileUploading:
          /*
          File is currently uploading
          stop the dialog closing
          disable all close buttons
        */
          this.dialogRef.disableClose = true;
          this.cancelButtonDisabled = true;
          break;
        case ImporterState.fileAggregating:
          this.tabs.forEach(tab => (tab.disabled = true));
          this.dialogRef.disableClose = false;
          this.cancelButtonDisabled = false;
          break;

        case ImporterState.fileAggregated:
          this.tabs.forEach(tab => (tab.disabled = false));
          this.dialogRef.disableClose = false;
          this.cancelButtonDisabled = false;
          break;
        default:
          this.tabs.forEach(tab => (tab.disabled = false));
          this.dialogRef.disableClose = false;
          this.cancelButtonDisabled = false;
      }

      if (importStatus === ImporterState.fileUploading) {
      } else {
      }
    });
  }

  public onSizeChanged(): void {
    this.menu.emitResize();
  }

  public showWindow() {
    if (this.windowRef) {
      return;
    }

    const windowOptions = {
      appendTo: this.containerRef,
      title: 'Conflicting Account Values',
      content: ImportConflictsComponent,
      width: window.innerWidth - 360,
      minWidth: 600,
      height: window.innerHeight - 300,
      minHeight: 400,
      autoFocusedElement: '#input-filter',
      keepContent: true
    };

    this.windowRef = this.windowService.open(windowOptions);

    this.subscriptions.push(
      this.windowRef.result.subscribe((result: IPollFileImport) => {
        this.windowRef = null;
        if (result && result.ImportID) {
          this.numberOfActiveConflicts = 0;
          this.updatedAccounts.emit(result);
        }
      })
    );
  }

  // prepareRoute(outlet: RouterOutlet) {
  //   return outlet && outlet.activatedRouteData && outlet.activatedRouteData.animation;
  // }

  /************************
   * Dialog Menu Controls *
   ************************/
  public onTabClicked(menuItem: IMenuItem) {
    // defect 15404 - Only show conflicts link in Import Data tab
    this.isMarketRate = menuItem.routerLink === 'market-rate-data';

    if (menuItem && menuItem.outlet && menuItem.routerLink) {
      const navigateParams = [{ outlets: { [menuItem.outlet]: [menuItem.routerLink] } }];
      this.router.navigate(navigateParams).then(nav => {
        /*
          If the result of our navigation was successful i.e. not blocked by
          a can deactivate, we will set this tab as selected.
        */
        if (nav) {
          this.tabs.forEach(tab => (tab.Selected = false));
          this.tabs.find(tab => tab.label === menuItem.label).Selected = true;

          this.location.replaceState(`/${menuItem.routerLink}`); // `/${menuItem.routerLink}`
        }
      });
    }
  }
}
